import {ActionIcon, Button, Checkbox, Group, Select, TextInput, Tooltip} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconInfoCircle, IconSearch, IconX} from '@tabler/icons-react'
import {useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import {RentalAssetTypeFormModal} from './rental-asset-type-form-modal'
import type {RentalAssetType} from './schemas'
import {
    TRACKING_MODE_FILTER_OPTIONS,
    TRACKING_MODE_HELP_TEXT,
    trackingModeLabel,
} from './tracking-mode-label'
import {useCreateRentalAssetType, useRentalAssetTypes} from './use-rental-asset-types'

function hasActiveFilters(name: string, trackingFilter: string | null) {
    return Boolean(name.trim()) || trackingFilter != null
}

function filterRentalAssetTypes(
    types: RentalAssetType[],
    nameFilter: string,
    trackingFilter: string | null,
) {
    const name = nameFilter.trim().toLowerCase()
    return types.filter((type) => {
        if (name && !type.name.toLowerCase().includes(name)) {
            return false
        }
        if (trackingFilter != null && String(type.isUniquelyIdentified) !== trackingFilter) {
            return false
        }
        return true
    })
}

function compareRentalAssetTypes(
    left: RentalAssetType,
    right: RentalAssetType,
    columnAccessor: DataTableSortStatus<RentalAssetType>['columnAccessor'],
) {
    const leftValue = String(left[columnAccessor as keyof RentalAssetType] ?? '')
    const rightValue = String(right[columnAccessor as keyof RentalAssetType] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortRentalAssetTypes(
    types: RentalAssetType[],
    sortStatus: DataTableSortStatus<RentalAssetType>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...types].sort(
        (left, right) =>
            directionMultiplier * compareRentalAssetTypes(left, right, sortStatus.columnAccessor),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

function trackingColumnTitle() {
    return (
        <Group gap={4} wrap="nowrap">
            Tracking
            <Tooltip label={TRACKING_MODE_HELP_TEXT} multiline w={320}>
                <ActionIcon
                    variant="transparent"
                    size="xs"
                    color="dimmed"
                    aria-label="Tracking mode definitions"
                    onClick={(event) => event.stopPropagation()}
                >
                    <IconInfoCircle size={14} stroke={1.5} />
                </ActionIcon>
            </Tooltip>
        </Group>
    )
}

export function RentalAssetTypesPage() {
    const navigate = useNavigate()
    const user = useCurrentUser()
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [trackingFilter, setTrackingFilter] = useState<string | null>(null)
    const [includeArchived, setIncludeArchived] = useState(false)
    const {data: rentalAssetTypes = [], isLoading} = useRentalAssetTypes({includeArchived})
    const createRentalAssetType = useCreateRentalAssetType()
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<RentalAssetType>>({
        columnAccessor: 'name',
        direction: 'asc',
    })
    const [createModalOpen, setCreateModalOpen] = useState(false)

    const filtersActive = hasActiveFilters(debouncedName, trackingFilter)

    const sortedTypes = useMemo(
        () =>
            sortRentalAssetTypes(
                filterRentalAssetTypes(rentalAssetTypes, debouncedName, trackingFilter),
                sortStatus,
            ),
        [rentalAssetTypes, debouncedName, trackingFilter, sortStatus],
    )

    const columns = useMemo((): DataTableColumn<RentalAssetType>[] => {
        return [
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Type name"
                        description="Show types whose names include the specified text"
                        placeholder="Search type names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'isUniquelyIdentified',
                title: trackingColumnTitle(),
                sortable: true,
                render: (record) => trackingModeLabel(record.isUniquelyIdentified),
                filter: (
                    <Select
                        label="Tracking"
                        description="Show types with the selected tracking mode"
                        placeholder="All tracking modes"
                        data={[...TRACKING_MODE_FILTER_OPTIONS]}
                        value={trackingFilter}
                        onChange={setTrackingFilter}
                        clearable
                    />
                ),
                filtering: trackingFilter != null,
            },
        ]
    }, [nameFilter, trackingFilter])

    return (
        <>
            <Group justify="space-between" mb="md">
                <Checkbox
                    label="Include archived"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.currentTarget.checked)}
                />
                {isAdmin(user) && (
                    <Button onClick={() => setCreateModalOpen(true)}>Create type</Button>
                )}
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedTypes}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedTypes.length === 0 ? 180 : undefined}
                noRecordsText={
                    filtersActive ? 'No types match your filters.' : 'No rental asset types yet.'
                }
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onRowClick={({record}) =>
                    void navigate({
                        to: '/rental-assets/types/$typeId',
                        params: {typeId: record.id},
                    })
                }
            />

            <RentalAssetTypeFormModal
                opened={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createRentalAssetType.mutateAsync(input)}
                onSaved={() => setCreateModalOpen(false)}
            />
        </>
    )
}
